import type { Question, UserAnswer, ExamConfig, ExamSession } from '../types';

/**
 * 判断作答是否与题目标准答案一致。
 * 多选题要求集合完全匹配（顺序无关）；单选题为严格相等。
 */
export function isAnswerCorrect(
  question: Pick<Question, 'correctAnswer'>,
  answer: string | string[]
): boolean {
  const correct = question.correctAnswer;

  if (Array.isArray(correct) && Array.isArray(answer)) {
    const sortedCorrect = [...correct].sort();
    const sortedAnswer = [...answer].sort();
    return (
      sortedCorrect.length === sortedAnswer.length &&
      sortedCorrect.every((v, i) => v === sortedAnswer[i])
    );
  }

  return correct === answer;
}

/**
 * 刷题引擎 - 管理答题流程
 */
export class QuizEngine {
  private session: ExamSession;
  private currentIndex: number = 0;
  private questions: Question[];

  constructor(config: ExamConfig) {
    this.questions = config.shuffle
      ? this.shuffleArray([...config.questions])
      : [...config.questions];

    this.session = {
      id: this.generateId(),
      configId: config.id,
      startTime: Date.now(),
      answers: [],
      status: 'in_progress',
    };
  }

  /**
   * 获取当前题目
   */
  getCurrentQuestion(): Question | null {
    return this.questions[this.currentIndex] || null;
  }

  /**
   * 获取题目总数
   */
  getTotalQuestions(): number {
    return this.questions.length;
  }

  /**
   * 获取当前题目索引（从0开始）
   */
  getCurrentIndex(): number {
    return this.currentIndex;
  }

  /**
   * 提交答案
   */
  submitAnswer(answer: string | string[], timeSpent: number): UserAnswer {
    const question = this.getCurrentQuestion();
    if (!question) {
      throw new Error('No current question');
    }

    const isCorrect = this.checkAnswer(question, answer);
    const userAnswer: UserAnswer = {
      questionId: question.id,
      answer,
      isCorrect,
      timeSpent,
      timestamp: Date.now(),
    };

    this.session.answers.push(userAnswer);
    return userAnswer;
  }

  /**
   * 跳转到下一题
   */
  nextQuestion(): Question | null {
    if (this.currentIndex < this.questions.length - 1) {
      this.currentIndex++;
      return this.getCurrentQuestion();
    }
    return null;
  }

  /**
   * 跳转到上一题
   */
  previousQuestion(): Question | null {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      return this.getCurrentQuestion();
    }
    return null;
  }

  /**
   * 跳转到指定题目
   */
  goToQuestion(index: number): Question | null {
    if (index >= 0 && index < this.questions.length) {
      this.currentIndex = index;
      return this.getCurrentQuestion();
    }
    return null;
  }

  /**
   * 完成考试
   */
  finish(): ExamSession {
    this.session.status = 'completed';
    this.session.endTime = Date.now();
    return this.session;
  }

  /**
   * 暂停考试
   */
  pause(): void {
    this.session.status = 'paused';
  }

  /**
   * 继续考试
   */
  resume(): void {
    this.session.status = 'in_progress';
  }

  /**
   * 获取当前会话
   */
  getSession(): ExamSession {
    return { ...this.session };
  }

  /**
   * 获取答题进度
   */
  getProgress(): { answered: number; total: number; percentage: number } {
    const answered = this.session.answers.length;
    const total = this.questions.length;
    return {
      answered,
      total,
      percentage: Math.round((answered / total) * 100),
    };
  }

  /**
   * 检查答案是否正确
   */
  private checkAnswer(question: Question, answer: string | string[]): boolean {
    return isAnswerCorrect(question, answer);
  }

  /**
   * 随机打乱数组
   */
  private shuffleArray<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  /**
   * 生成唯一ID
   */
  private generateId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
